import { useCallback, useEffect, useState } from "react";

import { ApiError, fetchFastApi } from "../api/fastapi";
import {
  OptroAccessErrorDetailSchema,
  OptroAccessFailure,
  OptroAccessKind,
  OptroAccessResponseSchema,
  OptroViewAccess,
  OptroViewAccessState,
} from "../models/audit/optroAccess";
import { useRespectOptroPermissions } from "./useRespectOptroPermissions";

// Map a refusal by its `code`, never its status: both sign-in-required and
// denied are 403s. A 403 with no Optro code is still a refusal Optro made —
// e.g. a revoked Optro grant, which the API answers with a bare
// `error_message` — and retrying can't fix it, so it reads as denied. A 404 is
// the id not being in this workspace; anything else (network drop, unexpected
// status or body) fails closed as unavailable.
const toFailure = (error: unknown): OptroAccessFailure => {
  if (!(error instanceof ApiError)) return "unavailable";
  const detail = OptroAccessErrorDetailSchema.safeParse(error.detailData);
  if (detail.success) {
    switch (detail.data.code) {
      case "optro_sign_in_required":
        return "signInRequired";
      case "optro_view_denied":
        return "denied";
      case "optro_access_unavailable":
        return "unavailable";
    }
  }
  if (error.status === 403) return "denied";
  if (error.status === 404) return "notFound";
  return "unavailable";
};

// A definitive answer that the entity must not be shown, as opposed to
// "still checking" or "couldn't check".
const isRefusal = (state: OptroViewAccessState): boolean =>
  state === "signInRequired" || state === "denied" || state === "notFound";

const checkAccess = async (
  kind: OptroAccessKind,
  id: string,
): Promise<OptroViewAccessState> => {
  try {
    const response = await fetchFastApi({
      method: "GET",
      path: `/optro-access/${kind}/${encodeURIComponent(id)}`,
      throwOnErrorEnvelope: true,
    });
    // Both statuses mean "render": not_linked is outside Optro's say.
    return OptroAccessResponseSchema.safeParse(response).success
      ? "allowed"
      : "unavailable";
  } catch (error) {
    return toFailure(error);
  }
};

/**
 * Whether the signed-in user may view a control / work step (or a session of
 * one), as Optro decides for linked entities.
 *
 * The answer is keyed on (kind, id, attempt): any change reads as "checking"
 * until its own answer arrives, so a previous entity's "allowed" can never let
 * the next one render. A missing id is "notFound". The 401 re-auth bounce is
 * handled globally by `fetchFastApi`.
 *
 * Behind `respect-optro-permissions`: with the flag off (or not yet known to
 * be on), always "allowed" without asking.
 *
 * Once an entity has read "allowed", a re-check of the same entity (e.g. the
 * flag turning on mid-session) runs in the background and keeps reading
 * "allowed" — unless it comes back as a definitive refusal — so an open page
 * with unsaved edits isn't unmounted by a spinner or a transient outage.
 */
export const useOptroViewAccess = (
  kind: OptroAccessKind,
  id: string | null | undefined,
): OptroViewAccess => {
  const enforcement = useRespectOptroPermissions();
  const [attempt, setAttempt] = useState(0);
  const [answer, setAnswer] = useState<{
    key: string;
    state: OptroViewAccessState;
  } | null>(null);

  const key = id ? `${kind}:${id}:${attempt}` : null;
  const entity = `${kind}:${id ?? ""}`;
  // The entity last read as allowed; see the doc comment.
  const [allowedEntity, setAllowedEntity] = useState<string | null>(null);

  useEffect(() => {
    if (enforcement !== "on" || !id) return;
    let cancelled = false;
    const requestKey = `${kind}:${id}:${attempt}`;
    void checkAccess(kind, id).then((state) => {
      if (!cancelled) setAnswer({ key: requestKey, state });
    });
    return () => {
      cancelled = true;
    };
  }, [enforcement, kind, id, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  let state: OptroViewAccessState = "checking";
  if (enforcement === "off") state = "allowed";
  else if (key === null) state = "notFound";
  else if (answer?.key === key) state = answer.state;

  // Adjusting state during render when what it derives from changes — the
  // documented React pattern; it re-renders once and settles.
  if (state === "allowed" && allowedEntity !== entity) {
    setAllowedEntity(entity);
  } else if (isRefusal(state) && allowedEntity === entity) {
    setAllowedEntity(null);
  }
  const recheckingAllowed =
    allowedEntity === entity &&
    (state === "checking" || state === "unavailable");

  return { state: recheckingAllowed ? "allowed" : state, retry };
};
